import type {
  AlgoliaProductType,
  CLPriceInfo,
} from '@integration-layer/components/Algolia/AlgoliaAisInfiniteHits.props'
import type { InstantSearch, UiState } from 'instantsearch.js/es/types'

/** Represents a timestamp as a number */
type Timestamp = number

/**
 * Creates a reactive state to track fetched SKUs with their timestamps.
 *
 * @returns {Ref<Object>} A reactive reference of fetched SKU codes and their fetch timestamps.
 */
export const useFetchedSkus = () =>
  useState<{ [sku: string]: Timestamp }>('plp_fetched_skus_record', () =>
    shallowRef({})
  )

/** Cache duration for SKU prices in milliseconds (30 minutes) */
const cacheTIme = 30 * 60 * 1000

/**
 * Checks if a timestamp is valid within the cache time.
 *
 * @param {Timestamp | undefined} timestamp - The timestamp to validate.
 * @returns {boolean} Whether the timestamp is within the cache duration.
 */
const isValid = (timestamp: Timestamp | undefined) =>
  !!timestamp && Date.now() - timestamp < cacheTIme

/**
 * Composable for managing product tile prices with caching mechanism.
 *
 * @description Provides functionality to fetch and cache SKU prices from a commerce layer.
 * @returns {Object} An object with methods and state for managing SKU prices.
 */
export default function useProductTileClPrices() {
  const { $cl } = useNuxtApp()

  /** A list of the SKUs already fetched in previous PLP pages */
  const fetchedSkus = useFetchedSkus()

  /**
   * Reactive state to store SKU prices.
   * @type {Ref<Record<string, CLPriceInfo>>}
   */
  const skuPrices = useState<Record<string, CLPriceInfo>>('plp_skus', () =>
    shallowRef({})
  )

  /**
   * Fetches the prices of SKUs and updates the `skuPrices` ref.
   *
   * @param {string[]} skuListParam - An array of SKU codes to fetch prices for.
   * @returns {Promise<void>}
   * @description
   * - Filters out SKUs that have been recently fetched
   * - Fetches prices in chunks of 10
   * - Updates the skuPrices state with new price information
   * - Caches fetch timestamps to prevent redundant requests
   */
  const fetchSkuPrices = async (skuListParam: string[]) => {
    if (!skuListParam.length) return
    const skusToFetch =
      skuListParam.filter(skuCode => !isValid(fetchedSkus.value[skuCode])) ?? []

    if (!skusToFetch.length) return

    const now = Date.now()
    const updatedFetchedSkus = { ...fetchedSkus.value }
    for (const sku of skusToFetch) {
      updatedFetchedSkus[sku] = now
    }
    fetchedSkus.value = updatedFetchedSkus
    const newPriceSkus = await Promise.all(
      chunk(skusToFetch, 25).map(skuListParam =>
        $cl.prices.list({
          include: ['sku'],
          filters: { sku_code_in: skuListParam.join(',') },
          pageSize: 25,
        })
      )
    ).then(responses => responses.flat())
    const newPricesMappedObject = newPriceSkus.reduce(
      (skuPricesBySkuCode, price) => {
        if (!price.sku_code) return skuPricesBySkuCode
        return {
          ...skuPricesBySkuCode,
          [price.sku_code]: {
            originalPrice: price?.compare_at_amount_float!,
            discountedPrice: price?.amount_float ?? '',
            discount: price?.amount_float
              ? percentageDiscount(
                  price?.compare_at_amount_float!,
                  price?.amount_float!
                )
              : '',
          },
        }
      },
      {}
    )

    skuPrices.value = {
      ...skuPrices.value,
      ...newPricesMappedObject,
    }
  }

  /**
   * Fetches SKU prices from an Algolia InstantSearch instance.
   * Same as fetchSkuPrices but takes as argument an AlgoliaInstantSearchInstance and the Algolia index name, and assignes the new prices to the skuPrices ref.
   * @param {InstantSearch<UiState, UiState>} instantSearchInstance - The Algolia InstantSearch instance.
   * @returns {Promise<void>}
   * @description
   * - Extracts SKU codes from Algolia search hits
   * - Calls fetchSkuPrices with extracted SKUs
   * - Designed to be used as an Algolia InstantSearch middleware
   */
  const fetchSkuPricesAlgoliaMiddleware = async (
    instantSearchInstance: InstantSearch<UiState, UiState>
  ) => {
    // wait for the shallow ref instance to be updated
    await nextTick()
    // get index name
    const index = instantSearchInstance.mainIndex.getIndexId()
    // access renderstate of infiniteHits of current index
    // as a fallback get latest results

    const hits = instantSearchInstance.renderState?.[index]?.infiniteHits?.hits
      .length
      ? instantSearchInstance.renderState?.[index]?.infiniteHits?.hits
      : instantSearchInstance.mainIndex.getResults()?.hits

    const hitsSkus =
      hits
        ?.map(item => item as unknown as AlgoliaProductType)
        ?.map(item => item?.size?.[0]?.SKU ?? null)
        ?.filter(code => isNonNullable(code)) ?? []

    await fetchSkuPrices(hitsSkus)
  }

  /**
   * Resets the fetched SKUs and SKU prices state.
   *
   * @returns {void}
   */
  const resetAll = () => {
    fetchedSkus.value = {}
    skuPrices.value = {}
  }

  return {
    skuPrices,
    fetchSkuPrices,
    fetchSkuPricesAlgoliaMiddleware,
    resetAll,
  }
}
